import React, { useEffect, useReducer, useRef, useState } from 'react'
import IconSection from '../IconSection'
import SimpleReactValidator from 'simple-react-validator'
import ChatGptService from 'services/chat-gpt.service'
import useAutosizeTextArea from 'hooks/useAutosizeTextArea'
import {
	faArrowLeft,
	faCheck,
	faExclamationCircle,
	faRobot,
	faSync,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RequiredSymbol from '../RequiredSymbol'

interface GenerateChallengeFormState {
	companyName: string
	companyDescription: string
	challengeDescription: string
}

interface GenerateChallengeFormProps {
	onGenerateStatusChange?: Function
}

const GenerateChallengeForm: React.FunctionComponent<GenerateChallengeFormProps> =
	({ onGenerateStatusChange }: GenerateChallengeFormProps) => {
		const [, forceUpdate] = useReducer((x) => x + 1, 0)
		const textAreaRef = useRef<HTMLTextAreaElement>(null)
		const [copied, setCopied] = useState<boolean>(false)
		const [result, setResult] = useState<string>('')
		const [error, setError] = useState<string>('')
		const [isGenerating, setIsGenerating] = useState(false)
		const [formState, setFormState] = useState<GenerateChallengeFormState>({
			companyName: '',
			companyDescription: '',
			challengeDescription: '',
		})
		const validatorConfig: any = {
			autoForceUpdate: { forceUpdate: forceUpdate },
			locale: 'en',
			element: (message: string) => (
				<p className="text-red-500 text-xs mt-1">{message}</p>
			),
		}
		const validator = useRef(new SimpleReactValidator(validatorConfig))

		const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
			const target = event.target as HTMLInputElement
			const value = target.value
			const name = target.name

			const newState = {
				...formState,
				[name]: value,
			}
			setFormState(newState)
		}

		const handleCopyToClipboard = () => {
			navigator.clipboard.writeText(result)
			setCopied(true)
		}

		const handleChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
			const val = evt.target?.value

			setResult(val)
		}

		const handleTextAreaChange = (
			event: React.FormEvent<HTMLTextAreaElement>
		) => {
			const target = event.target as HTMLTextAreaElement
			const value = target.value
			const name = target.name

			const newState = {
				...formState,
				[name]: value,
			}
			setFormState(newState)
		}

		const generateChallenge = async () => {
			if (!validator.current.allValid()) {
				return
			}
			setIsGenerating(true)
			if (onGenerateStatusChange) onGenerateStatusChange(true)
			try {
				const getLanguage = {
					message: `
						Detect the language of the assignment description: ${formState.challengeDescription}
							`,
				}
				const languageResponse = await ChatGptService.chatGpt(getLanguage)
				const language =
					languageResponse?.data?.attributes?.content?.trim() || 'English'
				const assignmentMessage = `
										Write the following assignment in ${language}: 

										You work for the company ${formState.companyName}. You need to write a title and a description for an assignment for a student team to work on. In summary, the assignment is this: ${formState.challengeDescription}.

										A short description about the company: ${formState.companyDescription}. 

										Please formulate an assignment title and assignment description, based on the template below (Word Count: Up to 250 words).

										TEMPLATE:
										Assignment title:
										Example 1: Help us develop / solve X by working on XYZ.
										Example 2: Help us change to a more XYZ organization.

										Assignment description:
										At "company X" we aim to help "customer segment Y" by solving "problem Z" with "solution W". We believe that we can reach this aim better if we could know / learn / implement / gain insight on ABC. We are looking for a team of talented students that is interested in helping us with the following questions:
										Question A
										Question B
										Question C
											`
				const params = { message: assignmentMessage }
				const res = await ChatGptService.chatGpt(params)
				setResult(res.data.attributes.content)
			} catch (e) {
				setError(
					'We could not generate your challenge at the moment, please try again later.'
				)
			}
			setIsGenerating(false)
			if (onGenerateStatusChange) onGenerateStatusChange(false)
		}

		useAutosizeTextArea(textAreaRef.current, result)

		// Init hook to triggr form validation on first load
		useEffect(() => {
			setFormState({ ...formState })
		}, [])

		return (
			<form className={`pt-6 mb-4 relative`} noValidate>
				<IconSection
					show={isGenerating}
					config={{
						color: 'text-purple-800',
						iconSpin: true,
						mainIcon: faRobot,
						subIcon: faSync,
					}}
				>
					<span className="text-gray-700">
						Generating your challenge description..
					</span>
				</IconSection>
				<IconSection
					show={error !== '' && !isGenerating}
					config={{
						color: 'text-red-800',
						iconSpin: false,
						mainIcon: faRobot,
						subIcon: faExclamationCircle,
					}}
				>
					<span className="text-red-700">{error}</span>
					<button
						type="button"
						onClick={() => setError('')}
						className="button bg-gray-50 mt-3 text-black border"
					>
						<FontAwesomeIcon icon={faArrowLeft} className="mr-2 opacity-75" />
						Back to form
					</button>
				</IconSection>

				<div className={isGenerating || result || error ? 'hidden' : ''}>
					<i className="block mb-6">
						Please note, the generator will match the language you will use.
					</i>
					<div className="mb-4">
						<label htmlFor="name">
							<RequiredSymbol />
							Company name
						</label>
						<input
							className="w-full"
							name="companyName"
							value={formState.companyName}
							onChange={handleInputChange}
							onBlur={() => validator.current.showMessageFor('company name')}
							id="companyName"
							type="text"
							placeholder="Company name"
						/>
						{validator.current.message(
							'company name',
							formState.companyName,
							'required|min:2'
						)}
					</div>
					<div className="mb-4">
						<label htmlFor="description">
							<RequiredSymbol />
							Brief one sentence company description
						</label>
						<textarea
							className="w-full"
							name="companyDescription"
							value={formState.companyDescription}
							onChange={handleTextAreaChange}
							onBlur={() =>
								validator.current.showMessageFor('company description')
							}
							id="companyDescription"
							placeholder="Company description"
						></textarea>
						{validator.current.message(
							'company description',
							formState.companyDescription,
							'required'
						)}
					</div>
					<div className="mb-2">
						<label htmlFor="description">
							<RequiredSymbol />
							One or two sentence description about the challenge you have
						</label>
						<textarea
							className="w-full"
							name="challengeDescription"
							value={formState.challengeDescription}
							onChange={handleTextAreaChange}
							onBlur={() =>
								validator.current.showMessageFor('challenge description')
							}
							id="challengeDescription"
							placeholder="Challenge description"
						></textarea>
						{validator.current.message(
							'challenge description',
							formState.challengeDescription,
							'required'
						)}
					</div>

					<button
						className="btn bg-purple-800 text-white"
						type="button"
						disabled={!validator.current.allValid()}
						onClick={() => generateChallenge()}
					>
						Generate
					</button>
				</div>

				<div className={`mb-6 ${result ? '' : 'hidden'}`}>
					<label htmlFor="result">
						Result<sup>*</sup>
					</label>
					<textarea
						ref={textAreaRef}
						className="w-full"
						rows={3}
						name="result"
						readOnly={!result}
						onChange={handleChange}
						value={result || 'Your result will appear here'}
						id="result"
					></textarea>
					{result && (
						<div className="mb-2 flex justify-between flex-wrap">
							<button
								type="button"
								onClick={() => setResult('')}
								className="button bg-gray-50 mt-2 text-black border"
							>
								<FontAwesomeIcon
									icon={faArrowLeft}
									className="mr-2 opacity-75"
								/>
								Try again
							</button>
							<button
								type="button"
								onClick={handleCopyToClipboard}
								className="button bg-yellow-400 mt-2 text-black"
							>
								{copied && (
									<FontAwesomeIcon icon={faCheck} className="mr-2 opacity-75" />
								)}{' '}
								{copied ? 'Copied' : 'Copy to clipboard'}
							</button>
						</div>
					)}
					<small className="text-gray-700">
						<sup>*</sup> This generator creates a title and a description using
						artificial intelligence and may contain inaccuracies. Verify
						information before use.
					</small>
				</div>
			</form>
		)
	}

export default GenerateChallengeForm
